import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RegistrationService } from 'src/app/api/registration.service';
import { RecruitingAllTimeLeaderboardResponse, RecruitingLeaderboardResponse, RegistrationNumbersForDatesResult } from 'src/app/api/types/registration';
import { UiModule } from 'src/app/ui/ui.module';
import { signal, WritableSignal } from '@angular/core';
import { Column, TableHeaderComponent, TableColumnComponent } from 'src/app/ui/tables';
import { IconButtonComponent } from 'src/app/ui/icon-button/icon-button.component';
import { IconService } from 'src/app/ui/icon.service';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { MessageService } from 'primeng/api';
import { ErrorService } from 'src/app/services/error.service';

interface LeaderboardTableData {
  rank: number;
  staffName: string;
  staffId?: number;
  participants: number;
  teams: number;
  week?: Date;
  month?: Date;
  year?: number;
  recordYear?: string;
  weekDate?: string;
  dateString?: string;
}

@Component({
  selector: 'app-registration-leaderboard',
  templateUrl: './registration-leaderboard.component.html',
  styleUrls: ['./registration-leaderboard.component.scss'],
  standalone: true,
  imports: [CommonModule, UiModule, TableColumnComponent, TableHeaderComponent, IconButtonComponent],
  providers: [DatePipe]
})
export class RegistrationLeaderboardComponent implements OnInit {
  loading = signal(false);
  error = signal<string | null>(null);

  allTimeLoading = signal(false);
  allTimeError = signal<string | null>(null);

  leaderboardData: RecruitingLeaderboardResponse | null = null;
  allTimeLeaderboardData: RecruitingAllTimeLeaderboardResponse | null = null;
  
  // Main tables data
  weekTableData: LeaderboardTableData[] = [];
  monthTableData: LeaderboardTableData[] = [];
  yearTableData: LeaderboardTableData[] = [];
  
  // Dialog tables data
  allTimeRecruiterWeekData: LeaderboardTableData[] = [];
  allTimeRecruiterMonthData: LeaderboardTableData[] = [];
  allTimeRecruiterYearData: LeaderboardTableData[] = [];
  
  // Dialog visibility
  showAllTimeDialog = false;
  currentDialogTable: 'week' | 'month' | 'year' = 'week';
  
  // Expand dialog visibility
  showExpandDialog = false;

  // Column definitions for for-loops
  weekMonthYearColumns: WritableSignal<Column<LeaderboardTableData>[]> = signal([
    { field: 'rank', header: '#', type: 'number', sortable: false, filterable: false },
    { field: 'staffName', header: 'Staff', type: 'text', sortable: false, filterable: false },
    { field: 'participants', header: 'Participants', type: 'number', sortable: false, filterable: false },
    { field: 'teams', header: 'Teams', type: 'number', sortable: false, filterable: false }
  ]);

  dialogColumns: WritableSignal<Column<LeaderboardTableData>[]> = signal([
    { field: 'rank', header: '#', type: 'number', sortable: false, filterable: false },
    { field: 'staffName', header: 'Staff', type: 'text', sortable: false, filterable: false },
    { field: 'dateString', header: 'Date', type: 'text', sortable: false, filterable: false },
    { field: 'participants', header: 'Participants', type: 'number', sortable: false, filterable: false },
    { field: 'teams', header: 'Teams', type: 'number', sortable: false, filterable: false }
  ]);

  constructor(
    private registrationService: RegistrationService,
    private datePipe: DatePipe,
    public icons: IconService,
    private messageService: MessageService,
    private errorService: ErrorService,
    private auth: LocalAuthService
  ) {}

  ngOnInit(): void {
    this.loadLeaderboardData();
    this.loadAllTimeLeaderboardData();
  }



  private loadLeaderboardData(): void {
    this.loading.set(true);
    this.registrationService.getRecruitingLeaderboardData().subscribe({
      next: (data: RecruitingLeaderboardResponse) => {
        this.leaderboardData = data;
        this.processTableData();
        this.loading.set(false);
      },
      error: (error: any) => {
        this.errorService.error('Failed to load leaderboard data', error, this.messageService);
        this.loading.set(false);
      }
    });
  }

  private loadAllTimeLeaderboardData(): void {
    this.allTimeLoading.set(true);
    this.registrationService.getRecruitingAllTimeLeaderboardData().subscribe({
      next: (data: RecruitingAllTimeLeaderboardResponse) => {
        this.allTimeLeaderboardData = data;
        this.processAllTimeTableData();
        this.allTimeLoading.set(false);
      },
      error: (error: any) => {
        this.errorService.error('Failed to load all time leaderboard data', error, this.messageService);
        this.allTimeLoading.set(false);
      }
    });
  }

  private processTableData(): void {
    if (!this.leaderboardData) return;

    this.weekTableData = this.transformTableData(this.leaderboardData.week, 'week');
    this.monthTableData = this.transformTableData(this.leaderboardData.month, 'month');
    this.yearTableData = this.transformTableData(this.leaderboardData.year, 'year');
  }

  private processAllTimeTableData(): void {
    if (!this.allTimeLeaderboardData) return;
    this.allTimeRecruiterWeekData = this.transformTableData(this.allTimeLeaderboardData.allTimeRecruiterWeek, 'week');
    this.allTimeRecruiterMonthData = this.transformTableData(this.allTimeLeaderboardData.allTimeRecruiterMonth, 'month');
    this.allTimeRecruiterYearData = this.transformTableData(this.allTimeLeaderboardData.allTimeRecruiterYear, 'year');
  }

  private transformTableData(data: RegistrationNumbersForDatesResult[], dateType: 'week' | 'month' | 'year'): LeaderboardTableData[] {
    if (!data || data.length === 0) {
      return [];
    }

    // Sort by participants (descending)
    const sortedData = [...data].sort((a, b) => b.totalOriginal - a.totalOriginal);

    // First map to LeaderboardTableData, then set dateString
    return sortedData.map((item, index) => {
      const mapped: LeaderboardTableData = {
        rank: index + 1,
        staffName: item.staffName || 'Unknown',
        staffId: item.staffId,
        participants: item.totalOriginal,
        teams: item.teamCount,
        week: item.week ? new Date(item.week.toString()) : undefined,
        month: item.month && item.year ? new Date(item.year, item.month - 1, 1) : undefined,
        year: item.year ? item.year : undefined,
        recordYear: item.year ? item.year.toString() : undefined,
        weekDate: item.week ? item.week.toString() : undefined,
      };
      return {
        ...mapped,
        dateString: this.setDateString(dateType, mapped)
      };
    });
  }

  setDateString(dateType: 'week' | 'month' | 'year', item: LeaderboardTableData): string {
    if (dateType === 'week') {
      return item.weekDate ? this.datePipe.transform(new Date(item.weekDate), 'MMM d, yyyy') || '' : '';
    } else if (dateType === 'month') {
      return item.month ? this.datePipe.transform(item.month, 'MMM yyyy') || '' : '';
    } else if (dateType === 'year') {
      return item.recordYear || '';
    } else {
      return '';
    }
  }

  showAllTimeTable(type: 'week' | 'month' | 'year'): void {
    this.currentDialogTable = type;
    this.showAllTimeDialog = true;
  }

  openExpandDialog(): void {
    this.showExpandDialog = true;
  }

  isCurrentUser(staffId?: number): boolean {
    const current = this.auth.currentUser;
    return !!staffId && !!current && staffId === current.newstaffId;
  }

  exportTableCSV(tableType: 'week' | 'month' | 'year'): void {
    let data: LeaderboardTableData[];
    switch (tableType) {
      case 'week':
        data = this.weekTableData;
        break;
      case 'month':
        data = this.monthTableData;
        break;
      case 'year':
        data = this.yearTableData;
        break;
    }

    // Create CSV content
    const headers = ['Rank', 'Staff', 'Participants', 'Teams'];
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        item.rank,
        `"${item.staffName}"`,
        item.participants,
        item.teams
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `registration-leaderboard-${tableType}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
} 